import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

import { Box, useTheme } from '@/shared/ui';

import type { Sx } from '@/shared/types/uiTypes';

import type { CatalogItem } from '@/entities/catalog-item';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import { isSameSymbol, isSameSymbolIgnoringPeriod } from '@/entities/symbol';
import { usePermission } from '@/features/_shared/user';
import {
  requestForwardCurveSelection,
  requestSpotSelection,
  SELECTOR_MODES
} from '@/features/symbol-selector';
import { shallowEqual, useAppDispatch, useAppSelector } from '@/store/hooks';

import { parseForwardCurveRowToSymbol } from '../../../lib/parseForwardCurveRowToSymbol';
import ForwardCatalogCell from './ForwardCatalogCell/ForwardCatalogCell';
import SpotCatalogCell from './SpotCatalogCell/SpotCatalogCell';

import type { Cell, Row } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';

const computeForwardSelection = (
  rowSymbols: SymbolKey[],
  selected: SymbolKey[],
  equals: (a: SymbolKey, b: SymbolKey) => boolean
) => {
  let any = false;
  let all = true;
  for (const sym of rowSymbols) {
    let found = false;
    for (const s of selected) {
      if (equals(s, sym)) {
        found = true;
        break;
      }
    }
    if (found) any = true;
    else all = false;
  }
  return { any, all };
};

const catalogRowSx = {
  display: 'flex',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  padding: 0,
  paddingLeft: '8px',
  '&[data-restricted="false"]:hover': {
    backgroundColor: 'table.highlight'
  }
} satisfies Sx;

interface CatalogTableRowProps {
  virtualRow: VirtualItem;
  row: Row<CatalogItem>;
  isForwardCurves: boolean;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
}

function CatalogTableRow({
  virtualRow,
  row,
  isForwardCurves,
  isInDashboard,
  renderFactsheetButton
}: CatalogTableRowProps) {
  const { palette } = useTheme();
  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewCatalogDetails);

  const isRestrictedData = useMemo(() => {
    if (!isPermissionGranted) return true;
    if (isForwardCurves) return false;
    const { Last, Change } = row.original;
    return !Last && !Change;
  }, [isForwardCurves, row.original, isPermissionGranted]);

  const symbol = useMemo(() => row.getValue<SymbolKey | undefined>('Symbol'), [row]);

  const symbols = useMemo((): SymbolKey[] => {
    if (!isForwardCurves) return [];
    return parseForwardCurveRowToSymbol(row);
  }, [isForwardCurves, row]);

  // Single selector for all selection state — one Redux subscription per row instead of
  // four, and avoids the stale-closure hazard of closing over derived values across calls.
  const { isSpotSelected, isAnyForwardSelected, isAllForwardSelected, isForwardCurveLocked } =
    useAppSelector(state => {
      const { symbols: selected, selectorMode } = state.symbolSelector;
      const forwardMode = selectorMode === SELECTOR_MODES.FORWARD_CURVE;
      const equals = forwardMode ? isSameSymbolIgnoringPeriod : isSameSymbol;

      const spotSelected = symbol ? selected.some(s => equals(s, symbol)) : false;

      let anyForward = false;
      let allForward = false;
      if (isForwardCurves && symbols.length > 0) {
        const fwd = computeForwardSelection(symbols, selected, equals);
        anyForward = fwd.any;
        allForward = fwd.all;
      }

      return {
        isSpotSelected: spotSelected,
        isAnyForwardSelected: anyForward,
        isAllForwardSelected: allForward,
        isForwardCurveLocked: selectorMode === SELECTOR_MODES.SPOT
      };
    }, shallowEqual);

  const backgroundColor = useMemo(() => {
    if (isForwardCurves) {
      if (isAnyForwardSelected) return palette.table.forwardCurve;
    } else if (isSpotSelected) {
      return palette.table.spot;
    }
    if (isRestrictedData) return palette.table.infoRowBg;
    return virtualRow.index % 2 === 0 ? palette.gx.tileLight : palette.gx.tileDark;
  }, [
    isForwardCurves,
    isAnyForwardSelected,
    isSpotSelected,
    isRestrictedData,
    virtualRow.index,
    palette
  ]);

  const dispatch = useAppDispatch();

  const handleRowClick = useCallback(() => {
    if (isRestrictedData) return;
    if (isForwardCurves) {
      if (!symbols.length) return;
      dispatch(requestForwardCurveSelection({ symbols }));
    } else if (symbol) {
      dispatch(requestSpotSelection({ symbol }));
    }
  }, [dispatch, isForwardCurves, isRestrictedData, symbol, symbols]);

  const renderCell = (cell: Cell<CatalogItem, unknown>) => {
    const cellKey = `${row.id}-${cell.id}`;
    if (isForwardCurves) {
      return (
        <ForwardCatalogCell
          key={cellKey}
          cell={cell}
          firstSelected={isAnyForwardSelected}
          isForwardCurveLocked={isForwardCurveLocked}
          isInDashboard={isInDashboard}
          isRestrictedData={isRestrictedData}
          renderFactsheetButton={renderFactsheetButton}
          secondSelected={isAllForwardSelected}
          symbols={symbols}
        />
      );
    }
    return (
      <SpotCatalogCell
        key={cellKey}
        cell={cell}
        isInDashboard={isInDashboard}
        isRestrictedData={isRestrictedData}
        renderFactsheetButton={renderFactsheetButton}
        symbol={symbol}
      />
    );
  };

  return (
    <Box
      key={row.id}
      data-index={virtualRow.index}
      data-restricted={String(isRestrictedData)}
      sx={[catalogRowSx, { backgroundColor }]}
      style={{
        transform: `translateY(${virtualRow.start}px)`,
        height: `${virtualRow.size}px`,
        cursor: isRestrictedData ? 'not-allowed' : 'pointer'
      }}
      onClick={handleRowClick}
    >
      {row.getVisibleCells().map(renderCell)}
    </Box>
  );
}

export default CatalogTableRow;
